import { InitialRequestConfig } from "../models/flexible/initial-request";
import { GeneralFieldController } from "./general-field-controller";
import { GET_SCOPES, whereDrugs } from "./queries";
import { relevantIndicationDisplay } from "../utils/base-agreement-formatters";
import { useAvailableRelevantIndications } from "../hooks/useAvailableRelevantIndications";
import { useFormikContext, useFieldValue } from "formik";
import { RACADataPoints } from "../models/flexible/ca-data-points";
import { useEffect } from "react";
import { addDays, isAfter, isBefore, isEqual } from "date-fns";
import { FlexControlProps } from "../models/flexible/flex-field-controller";
import { Box, Typography } from "@material-ui/core";
import { Article71Indication, CoverageRequestTherapyDecision, Maybe } from "../models/generated/graphql";
import { useTranslation } from "react-i18next";
import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { byDesc, parseDate, sortByComparisons } from "@lyfegen-platform/utils";
import { useScopeFields } from "../hooks/useScopeFields";
import * as Yup from "yup";
import { isStartingPoint } from "../models/flexible/typed-flex-entity";

enum ControlState {
	DISABLED = "disabled",
	ENABLED = "enabled",
}

export type ExtendedArticle71Indication = Article71Indication & {
	appendix?: string;
};

function compare(a: { id: string }, b: { id: string }) {
	return a.id === b.id;
}

const getIsIndicationValidAtTheEndOfCoveredPeriod = (
	relevantIndicationStart: Date,
	relevantIndicationEnd: Date | undefined,
	periodEndDate: Date
) => {
	// Main Indication has to start max on the last day of therapy
	return (
		(isBefore(relevantIndicationStart, periodEndDate) || isEqual(relevantIndicationStart, periodEndDate)) &&
		// If the mainIndication has validToDate, it has to be min on the last day of therapy trial,
		(relevantIndicationEnd
			? isAfter(relevantIndicationEnd, periodEndDate) || isEqual(relevantIndicationEnd, periodEndDate)
			: // if evergreen - it has to start before therapy end date
			  isBefore(relevantIndicationStart, periodEndDate) || isEqual(relevantIndicationStart, periodEndDate))
	);
};

function RelevantIndicationDisclaimer(props: { mainIndications: Array<ExtendedArticle71Indication> }) {
	const { t } = useTranslation();
	const [decisionTherapy] = useFieldValue(AttributeIdentifiers.DecisionTherapy);
	const [therapyTrialEndDate] = useFieldValue(AttributeIdentifiers.TherapyTrialEndDate);
	const [therapyTrialStartDate] = useFieldValue(AttributeIdentifiers.TherapyTrialStartDate);
	const [coverageEndDate] = useFieldValue(AttributeIdentifiers.CoverageEndDate);
	const [coverageStartDate] = useFieldValue(AttributeIdentifiers.CoverageStartDate);

	// The message will not be shown if there isnt enough data
	if (
		!decisionTherapy ||
		decisionTherapy === CoverageRequestTherapyDecision.Declined ||
		(decisionTherapy === CoverageRequestTherapyDecision.Trial && (!therapyTrialEndDate || !therapyTrialStartDate)) ||
		(decisionTherapy === CoverageRequestTherapyDecision.Approved && !coverageStartDate)
	) {
		return <></>;
	}

	const fallbackIndications = props.mainIndications
		.filter((ind) => {
			// Main Indication must contain at least validFromDate
			if (!ind.mainIndication || !ind.mainIndication?.validFromDate) {
				return false;
			}

			const relevantIndicationStart = parseDate(ind.mainIndication?.validFromDate);

			// End Date for Main Indication is optional, evergreen is assumed
			const relevantIndicationEnd = ind.mainIndication?.validToDate ? parseDate(ind.mainIndication?.validToDate) : undefined;

			switch (decisionTherapy) {
				case CoverageRequestTherapyDecision.Trial: {
					const endDate = parseDate(therapyTrialEndDate);
					return getIsIndicationValidAtTheEndOfCoveredPeriod(relevantIndicationStart, relevantIndicationEnd, endDate);
				}

				case CoverageRequestTherapyDecision.Approved: {
					// If main indication and coverage are evergreen - main indication start max on the coverage start
					const coverageStart = parseDate(coverageStartDate);

					if (!relevantIndicationEnd && !coverageEndDate) {
						return isBefore(relevantIndicationStart, coverageStart) || isEqual(relevantIndicationStart, coverageStart);
					}
					// If coverage is evergreen but main indication not - main indication valid on the day of the coverage startday
					if (relevantIndicationEnd && !coverageEndDate) {
						return (
							(isBefore(relevantIndicationStart, coverageStart) || isEqual(relevantIndicationStart, coverageStart)) &&
							(isAfter(relevantIndicationEnd, coverageStart) || isEqual(relevantIndicationEnd, coverageStart))
						);
					}
					// If main indication and coverage have end dates - main indication ends max on the coverage end
					const coverageEnd = parseDate(coverageEndDate);
					return getIsIndicationValidAtTheEndOfCoveredPeriod(relevantIndicationStart, relevantIndicationEnd, coverageEnd);
				}

				default:
					return false;
			}
		})
		.sort(sortByComparisons(byDesc((fi) => fi.mainIndication?.validFromDate)));

	if (fallbackIndications.length === 0 || !fallbackIndications[0]?.name) {
		return <></>;
	}

	return (
		<Box mb={1} display={"flex"} alignItems={"center"}>
			<Typography>{`${t("art71-relevant-empty", { indication: fallbackIndications[0]?.name })}`}</Typography>
		</Box>
	);
}

// For RACA Forms, Therapy Trial is expected to last 60 days, but the end value can be edited
const TherapyTrialAndMultiBrandsRelatedEffect = (props: {
	isEditable: boolean;
	context: any;
	options: Array<Article71Indication> | null;
}) => {
	const { setFieldValue, values } = useFormikContext<RACADataPoints>();
	const { relevantIndication, generics } = useScopeFields(props.context);

	useEffect(() => {
		if (props.isEditable && values.decisionTherapy === CoverageRequestTherapyDecision.Trial) {
			if (values.therapyTrialStartDate) {
				const therapyStartDatePlus60Days = addDays(parseDate(values.therapyTrialStartDate), 60);
				setFieldValue(AttributeIdentifiers.TherapyTrialEndDate, therapyStartDatePlus60Days);
			}
		}
	}, [props.isEditable, values.decisionTherapy, values.therapyTrialStartDate]);

	useEffect(() => {
		if (relevantIndication && props.options) {
			if (!props.options.some((relIndication) => relIndication.id === relevantIndication?.id)) {
				setFieldValue(AttributeIdentifiers.RelevantIndication, null);
			}
		}
	}, [relevantIndication, generics]);

	return null;
};

export class RelevantIndicationFieldController extends GeneralFieldController {
	#options: Array<ExtendedArticle71Indication> | Array<string> | null = null;

	getInitialRequestComponents?(): InitialRequestConfig {
		const { entityId = "", patientId = this.parent.context.flexEntity?.data[AttributeIdentifiers.PatientId] } =
			this.parent.context.parameters;

		if (entityId) {
			return {
				queries: [GET_SCOPES],
				variables: {
					entityId,
					where: whereDrugs(this.parent.context.entityType.id, this.parent.context.tenantId),
				},
			};
		}

		if (patientId) {
			return {
				queries: [GET_SCOPES],
				variables: {
					patientId,
					where: whereDrugs(this.parent.context.entityType.id, this.parent.context.tenantId),
				},
			};
		}

		return { queries: [], variables: {} };
	}

	override mapInitialValue(value: unknown): unknown {
		const hasRelevantIndication = !!(
			this.parent.context.cases?.length &&
			this.parent.context.startingPoint?.data[AttributeIdentifiers.RelevantIndication] &&
			this.parent.context.cases.some((cs) => !!cs.relevantArticle71Indication)
		);

		return hasRelevantIndication && this.parent.context.isEditable ? this.parent.context.startingPoint?.data[AttributeIdentifiers.RelevantIndication] : value;
	}

	override useControlState(): ControlState {
		const { options } = useAvailableRelevantIndications(this, this.path);

		if (options.length < 1) {
			return ControlState.DISABLED;
		}

		return !this.parent.context.cases?.length || isStartingPoint(this.parent.context.flexEntity)
			? ControlState.ENABLED
			: ControlState.DISABLED;
	}

	override useControlProps(path: string): Maybe<FlexControlProps> | undefined {
		const { options } = useAvailableRelevantIndications(this, path);
		this.options = options;

		return {
			autoSelect: false,
			controlType: "typeahead",
			options,
			property: "id",
			isChildInFormik: true,
			getOptionLabel: (option: ExtendedArticle71Indication) => relevantIndicationDisplay(option),
			getOptionSelected: compare,
			groupBy: (option: any) => option.group,
		};
	}

	protected override createFieldValidationRuleByDataType(): Yup.AnySchema {
		return Yup.mixed().nullable();
	}

	override getValueTypeProps() {
		return {
			controlType: "typeahead",
			property: "name",
		};
	}

	renderBeforeContent() {
		return (
			<TherapyTrialAndMultiBrandsRelatedEffect
				isEditable={this.parent.context.isEditable}
				context={this.parent.context}
				options={this.options}
			/>
		);
	}

	renderAfter() {
		if (!this.options) {
			return <></>;
		}

		const mainIndications = this.options.filter((option) => !!option.mainIndication && !!option.mainIndication.validFromDate);
		if (mainIndications.length < 1) {
			return <></>;
		}

		return <RelevantIndicationDisclaimer mainIndications={mainIndications} />;
	}

	set options(options: Array<any> | null) {
		this.#options = options;
	}

	get options() {
		return this.#options;
	}
}
